import { useEffect, useMemo, useState } from "react";

import {
  decodeRoutePolylinePositions,
  Log,
  OsrmApi,
  TLatLngExpression,
  TLatLngTuple,
} from "@ryde/shared";

import { IPlaybackModel } from "./investigation.models";

/**
 * Planned-route positions for the investigation map. Prefers the ride's stored route polyline; when the
 * ride has none (many course rides never persist it — only station coords + a distance), it fetches the
 * road-following route from OSRM — the same service the ride-edit map uses — so the line matches
 * edit-ride instead of drawing a straight station-to-station connector.
 */
export const usePlannedRoutePositions = (
  model?: IPlaybackModel,
): TLatLngExpression[] => {
  const [computed, setComputed] = useState<TLatLngExpression[]>([]);
  const [osrmFailed, setOsrmFailed] = useState(false);

  const stored = useMemo<TLatLngExpression[]>(() => {
    const polyline = model?.payload.route.plannedPolyline;

    return polyline
      ? (decodeRoutePolylinePositions(polyline) as TLatLngExpression[])
      : [];
  }, [model]);

  const stationLatLngs = useMemo<TLatLngTuple[]>(
    () =>
      model
        ? model.stations
            .filter(station => station.latLng)
            .map(station => station.latLng as TLatLngTuple)
        : [],
    [model],
  );

  // Stable signature of the route inputs — changes only when the polyline or stations genuinely change,
  // so the effect (and its state resets) fire only on a real change, not on every polling re-render.
  const routeKey = useMemo(
    () =>
      `${model?.payload.route.plannedPolyline ?? ""}::${stationLatLngs
        .map(([lat, lng]) => `${lat},${lng}`)
        .join(";")}`,
    [model, stationLatLngs],
  );

  useEffect(() => {
    setComputed([]);
    setOsrmFailed(false);

    // Only compute when there is no stored polyline and there are ≥2 stations to route between.
    if (stored.length > 1 || stationLatLngs.length <= 1) {
      return undefined;
    }

    let cancelled = false;

    const computeRoute = async (): Promise<void> => {
      // OSRM expects [lng, lat] pairs.
      const points = stationLatLngs.map(([lat, lng]) => [lng, lat]);

      try {
        const polyline = await OsrmApi.getRoutePolyline(points);

        if (cancelled) {
          return;
        }

        if (polyline) {
          setComputed(
            decodeRoutePolylinePositions(polyline) as TLatLngExpression[],
          );
        } else {
          setOsrmFailed(true);
        }
      } catch (error) {
        Log.error("Failed to compute investigation planned route", error);

        if (!cancelled) {
          setOsrmFailed(true);
        }
      }
    };

    void computeRoute();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  // Priority: stored polyline → OSRM-computed road route.
  if (stored.length > 1) {
    return stored;
  }

  if (computed.length > 1) {
    return computed;
  }

  // While the OSRM route is still loading, draw nothing (avoids a straight line flashing then
  // snapping to the road route). Only fall back to a straight connector if OSRM actually failed.
  const needsOsrmRoute = stationLatLngs.length > 1;

  if (needsOsrmRoute && !osrmFailed) {
    return [];
  }

  return stationLatLngs as TLatLngExpression[];
};
